import React, { useCallback, useRef, useEffect } from 'react';
import { TextField } from '@mui/material';
import { toEnglishDigits, cleanNumericInput } from '../utils/numberConverter';

/**
 * TextField سفارشی که اعداد فارسی رو می‌گیره
 * با type="text" و inputMode="numeric" برای کنترل کامل cursor
 */
const CustomTextField = React.forwardRef((props, ref) => {
  const { 
    onChange, 
    type, 
    InputProps, 
    inputProps,
    value,
    ...otherProps 
  } = props;

  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  // اعمال cursor position بعد از render
  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      const input = inputRef.current.querySelector('input');
      if (input && document.activeElement === input) {
        try {
          input.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
        } catch (e) {
          // ignore
        }
        cursorPositionRef.current = null;
      }
    }
  });

  // تبدیل کاراکتر فارسی به انگلیسی
  const convertPersianToEnglish = useCallback((char) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    
    const persianIndex = persianDigits.indexOf(char);
    if (persianIndex !== -1) return persianIndex.toString();
    
    const arabicIndex = arabicDigits.indexOf(char);
    if (arabicIndex !== -1) return arabicIndex.toString();
    
    return char;
  }, []);

  // Handle کردن beforeinput با حفظ cursor
  const handleBeforeInput = useCallback((event) => {
    // فقط برای فیلدهای عددی
    if (type !== 'number' && type !== 'tel') return;

    const char = event.data;
    if (!char) return;

    const converted = convertPersianToEnglish(char);
    
    if (char !== converted) {
      event.preventDefault();
      
      const input = event.target;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value || '';
      
      const newValue = 
        currentValue.substring(0, start) + 
        converted + 
        currentValue.substring(end);
      
      // ذخیره cursor position
      cursorPositionRef.current = start + 1;
      
      if (onChange) {
        const syntheticEvent = {
          target: {
            ...input,
            value: newValue,
            name: input.name
          },
          currentTarget: input
        };
        onChange(syntheticEvent);
      }
    }
  }, [type, convertPersianToEnglish, onChange]);

  // Handle کردن paste
  const handlePaste = useCallback((event) => {
    if (type !== 'number' && type !== 'tel') return;

    const pastedText = event.clipboardData.getData('text');
    const convertedText = toEnglishDigits(pastedText);
    
    if (pastedText !== convertedText) {
      event.preventDefault();
      
      const input = event.target;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value || '';
      
      const newValue = 
        currentValue.substring(0, start) + 
        convertedText + 
        currentValue.substring(end);
      
      // ذخیره cursor position
      cursorPositionRef.current = start + convertedText.length;
      
      if (onChange) {
        const syntheticEvent = {
          target: {
            ...input,
            value: newValue,
            name: input.name
          },
          currentTarget: input
        };
        onChange(syntheticEvent);
      }
    }
  }, [type, onChange]);

  // Handle کردن onChange
  const handleChange = useCallback((event) => {
    if (!onChange) return;
    
    const originalValue = event.target.value;
    let convertedValue = toEnglishDigits(originalValue);
    
    // فیلتر کردن برای فیلدهای عددی
    if (type === 'number' || type === 'tel') {
      convertedValue = convertedValue.replace(/[^0-9.]/g, '');
    }
    
    if (convertedValue !== originalValue) {
      const newEvent = {
        ...event,
        target: {
          ...event.target,
          value: convertedValue
        }
      };
      onChange(newEvent);
    } else {
      onChange(event);
    }
  }, [onChange, type]);

  // تبدیل type="number" به type="text" برای کنترل cursor
  const actualType = (type === 'number' || type === 'tel') ? 'text' : type;

  // ترکیب inputProps
  const mergedInputProps = React.useMemo(() => {
    const baseProps = {
      ...(inputProps || {}),
      onBeforeInput: handleBeforeInput,
      onPaste: handlePaste,
    };
    
    // برای فیلدهای عددی
    if (type === 'number' || type === 'tel') {
      baseProps.inputMode = 'decimal'; // keyboard عددی در موبایل
      baseProps.pattern = '[0-9]*';
      baseProps.style = {
        ...(inputProps?.style || {}),
        direction: 'ltr',
        textAlign: 'left'
      };
    }
    
    return baseProps;
  }, [inputProps, type, handleBeforeInput, handlePaste]);

  // ترکیب InputProps
  const mergedInputProps2 = React.useMemo(() => {
    if (!InputProps) return InputProps;
    
    const baseProps = { ...InputProps };
    
    if (type === 'number' || type === 'tel') {
      baseProps.sx = {
        ...(InputProps.sx || {}),
        direction: 'ltr',
        '& input': {
          textAlign: 'left'
        }
      };
    }
    
    return baseProps;
  }, [InputProps, type]);

  return (
    <TextField
      ref={(node) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      type={actualType}
      value={value}
      onChange={handleChange}
      InputProps={mergedInputProps2}
      inputProps={mergedInputProps}
      {...otherProps}
    />
  );
});

CustomTextField.displayName = 'CustomTextField';

export default CustomTextField;